/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  ViewEncapsulation,
  ViewChild,
  ChangeDetectorRef,
  Attribute,
  OnInit,
  Inject
} from '@angular/core';
import {MdOption, MdOptionSelectionChange, MdOptgroup} from '../core/option/index';
import {ENTER, SPACE, UP_ARROW, DOWN_ARROW, HOME, END} from '../core/keyboard/keycodes';
import {FocusKeyManager} from '../core/a11y/focus-key-manager';
import {Dir} from '../core/rtl/dir';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import {transformPlaceholder, transformPanel, fadeInContent} from './select-animations';
import {ControlValueAccessor, NgControl} from '@angular/forms';
import {coerceBooleanProperty} from '../core/coercion/boolean-property';
import {ConnectedOverlayDirective} from '../core/overlay/overlay-directives';
import {ViewportRuler} from '../core/overlay/position/viewport-ruler';
import {SelectionModel} from '../core/selection/selection';
import {getMdSelectDynamicMultipleError, getMdSelectNonArrayValueError} from './select-errors';
import'rxjs/add/observable/merge';
import'rxjs/add/operator/startWith';
import'rxjs/add/operator/filter';
import {CanColor, mixinColor} from '../core/common-behaviors/color';
import {CanDisable, mixinDisabled} from '../core/common-behaviors/disabled';
import {
  FloatPlaceholderType,
  PlaceholderOptions,
  MD_PLACEHOLDER_GLOBAL_OPTIONS} from '../core/placeholder/placeholder-options';
/**
 * The fixed height of every option element (option, group header etc.).
 */
export const SELECT_ITEM_HEIGHT = 48;
/**
 * The max height of the select's overlay panel
 */
export const SELECT_PANEL_MAX_HEIGHT = 256;
/**
 * The max number of options visible at once in the select panel.
 */
export const SELECT_MAX_OPTIONS_DISPLAYED =
    Math.floor(SELECT_PANEL_MAX_HEIGHT / SELECT_ITEM_HEIGHT);
/**
 * The fixed height of the select's trigger element.
 */
export const SELECT_TRIGGER_HEIGHT = 30;
/**
 * Must adjust for the difference in height between the option and the trigger,
 * so the text will align on the y axis.
 */
export const SELECT_OPTION_HEIGHT_ADJUSTMENT = (SELECT_ITEM_HEIGHT - SELECT_TRIGGER_HEIGHT) / 2;
/**
 * The panel's padding on the x-axis
 */
export const SELECT_PANEL_PADDING_X = 16;
/**
 * The panel's x axis padding if it is indented (e.g. there is an option group).
 */
export const SELECT_PANEL_INDENT_PADDING_X = SELECT_PANEL_PADDING_X * 2;
/**
 * Distance between the panel edge and the option text in
 * multi-selection mode.
 * 
 * (SELECT_PADDING * 1.75) + 20 = 48
 * The padding is multiplied by 1.75 because the checkbox's margin is half the padding, and
 * the browser adds ~4px, because we're using inline elements.
 * The checkbox width is 20px.
 */
export const SELECT_MULTIPLE_PANEL_PADDING_X = SELECT_PANEL_PADDING_X * 1.75 + 20;
/**
 * The panel's padding on the y-axis. This padding indicates there are more
 * options available if you scroll.
 */
export const SELECT_PANEL_PADDING_Y = 16;
/**
 * The select panel will only "fit" inside the viewport if it is positioned at
 * this value or more away from the viewport boundary.
 */
export const SELECT_PANEL_VIEWPORT_PADDING = 8;
/**
 * Change event object that is emitted when the select value has changed.
 */
export class MdSelectChange {
/**
 * @param {?} source
 * @param {?} value
 */
constructor(public source: MdSelect,
public value: any) { }
}

function MdSelectChange_tsickle_Closure_declarations() {
/** @type {?} */
MdSelectChange.prototype.source;
/** @type {?} */
MdSelectChange.prototype.value;
}

export class MdSelectBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdSelectBase_tsickle_Closure_declarations() {
/** @type {?} */
MdSelectBase.prototype._renderer;
/** @type {?} */
MdSelectBase.prototype._elementRef;
}

export const /** @type {?} */ _MdSelectMixinBase = mixinColor(mixinDisabled(MdSelectBase), 'primary');
export class MdSelect extends _MdSelectMixinBase implements AfterContentInit, OnDestroy, OnInit,
    ControlValueAccessor, CanColor, CanDisable {
/**
 * Whether or not the overlay panel is open.
 */
private _panelOpen = false;
/**
 * Subscriptions to option events.
 */
private _optionSubscription: Subscription;
/**
 * Subscription to changes in the option list.
 */
private _changeSubscription: Subscription;
/**
 * Subscription to tab events while overlay is focused.
 */
private _tabSubscription: Subscription;
/**
 * Whether filling out the select is required in the form.
 */
private _required: boolean = false;
/**
 * The scroll position of the overlay panel, calculated to center the selected option.
 */
private _scrollTop = 0;
/**
 * The placeholder displayed in the trigger of the select.
 */
private _placeholder: string;
/**
 * Whether the component is in multiple selection mode.
 */
private _multiple: boolean = false;
/**
 * Deals with the selection logic.
 */
_selectionModel: SelectionModel<MdOption>;
/**
 * The animation state of the placeholder.
 */
private _placeholderState = '';
/**
 * Tab index for the element.
 */
private _tabIndex: number;
/**
 * Deals with configuring placeholder options
 */
private _placeholderOptions: PlaceholderOptions;
/**
 * The width of the trigger. Must be saved to set the min width of the overlay panel
 * and the width of the selected value.
 */
_triggerWidth: number;
/**
 * Manages keyboard events for options in the panel.
 */
_keyManager: FocusKeyManager;
/**
 * The width of the selected option's value. Must be set programmatically
 * to ensure its overflow is clipped, as it's absolutely positioned.
 */
_selectedValueWidth: number;
/**
 * View -> model callback called when value changes
 */
_onChange: (value: any) => void = () => {};
/**
 * View -> model callback called when select has been touched
 */
_onTouched = () => {};
/**
 * The IDs of child options to be passed to the aria-owns attribute.
 */
_optionIds: string = '';
/**
 * The value of the select panel's transform-origin property.
 */
_transformOrigin: string = 'top';
/**
 * Whether the panel's animation is done.
 */
_panelDoneAnimating: boolean = false;
/**
 * The y-offset of the overlay panel in relation to the trigger's top start corner.
 * This must be adjusted to align the selected option text over the trigger text.
 * when the panel opens. Will change based on the y-position of the selected option.
 */
_offsetY = 0;
/**
 * This position config ensures that the top "start" corner of the overlay
 * is aligned with with the top "start" of the origin by default (overlapping
 * the trigger completely). If the panel cannot fit below the trigger, it
 * will fall back to a position above the trigger.
 */
_positions = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];
/**
 * Trigger that opens the select.
 */
trigger: ElementRef;
/**
 * Overlay pane containing the options.
 */
overlayDir: ConnectedOverlayDirective;
/**
 * All of the defined select options.
 */
options: QueryList<MdOption>;
/**
 * All of the defined groups of options.
 */
optionGroups: QueryList<MdOptgroup>;
/**
 * Classes to be passed to the select panel. Supports the same syntax as `ngClass`.
 */
panelClass: string|string[]|Set<string>|{[key: string]: any};
/**
 * Placeholder to be shown if no value has been selected.
 * @return {?}
 */
get placeholder() { return this._placeholder; }
/**
 * @param {?} value
 * @return {?}
 */
set placeholder(value: string) {
    this._placeholder = value;

    // Must wait to record the trigger width to ensure placeholder width is included.
    Promise.resolve(null).then(() => this._setTriggerWidth());
  }
/**
 * Whether the component is required.
 * @return {?}
 */
get required() { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value: any) { this._required = coerceBooleanProperty(value); }
/**
 * Whether the user should be allowed to select multiple options.
 * @return {?}
 */
get multiple(): boolean { return this._multiple; }
/**
 * @param {?} value
 * @return {?}
 */
set multiple(value: boolean) {
    if (this._selectionModel) {
      throw getMdSelectDynamicMultipleError();
    }

    this._multiple = coerceBooleanProperty(value);
  }
/**
 * Whether to float the placeholder text.
 * @return {?}
 */
get floatPlaceholder(): FloatPlaceholderType { return this._floatPlaceholder; }
/**
 * @param {?} value
 * @return {?}
 */
set floatPlaceholder(value: FloatPlaceholderType) {
    this._floatPlaceholder = value || this._placeholderOptions.float || 'auto';
  }
private _floatPlaceholder: FloatPlaceholderType;
/**
 * Tab index for the select element.
 * @return {?}
 */
get tabIndex(): number { return this.disabled ? -1 : this._tabIndex; }
/**
 * @param {?} value
 * @return {?}
 */
set tabIndex(value: number) {
    if (typeof value !== 'undefined') {
      this._tabIndex = value;
    }
  }
/**
 * Aria label of the select. If not specified, the placeholder will be used as label.
 */
ariaLabel: string = '';
/**
 * Input that can be used to specify the `aria-labelledby` attribute.
 */
ariaLabelledby: string = '';
/**
 * Combined stream of all of the child options' change events.
 * @return {?}
 */
get optionSelectionChanges(): Observable<MdOptionSelectionChange> {
    return Observable.merge(...this.options.map(option => option.onSelectionChange));
  }
/**
 * Event emitted when the select has been opened.
 */
onOpen: EventEmitter<void> = new EventEmitter<void>();
/**
 * Event emitted when the select has been closed.
 */
onClose: EventEmitter<void> = new EventEmitter<void>();
/**
 * Event emitted when the selected value has been changed by the user.
 */
change: EventEmitter<MdSelectChange> = new EventEmitter<MdSelectChange>();
/**
 * @param {?} _viewportRuler
 * @param {?} _changeDetectorRef
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} _dir
 * @param {?} _control
 * @param {?} tabIndex
 * @param {?} placeholderOptions
 */
constructor(
private _viewportRuler: ViewportRuler,
private _changeDetectorRef: ChangeDetectorRef,
    renderer: Renderer2,
    elementRef: ElementRef,
private _dir: Dir,
public _control: NgControl,
     tabIndex: string,
      placeholderOptions: PlaceholderOptions) {
    super(renderer, elementRef);

    if (this._control) {
      this._control.valueAccessor = this;
    }

    this._tabIndex = parseInt(tabIndex) || 0;

    this._placeholderOptions = placeholderOptions ? placeholderOptions : {};
    this.floatPlaceholder = this._placeholderOptions.float || 'auto';
  }
/**
 * @return {?}
 */
ngOnInit() {
    this._selectionModel = new SelectionModel<MdOption>(this.multiple, null, false);
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._initKeyManager();

    this._changeSubscription = this.options.changes.startWith(null).subscribe(() => {
      this._resetOptions();

      if (this._control) {
        // Defer setting the value in order to avoid the "Expression
        // has changed after it was checked" errors from Angular.
        Promise.resolve(null).then(() => this._setSelectionByValue(this._control.value));
      }
    });
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._dropSubscriptions();

    if (this._changeSubscription) {
      this._changeSubscription.unsubscribe();
    }

    if (this._tabSubscription) {
      this._tabSubscription.unsubscribe();
    }
  }
/**
 * Toggles the overlay panel open or closed.
 * @return {?}
 */
toggle(): void {
    this.panelOpen ? this.close() : this.open();
  }
/**
 * Opens the overlay panel.
 * @return {?}
 */
open(): void {
    if (this.disabled || !this.options.length) {
      return;
    }

    if (!this._triggerWidth) {
      this._setTriggerWidth();
    }

    this._calculateOverlayPosition();
    this._placeholderState = this._floatPlaceholderState();
    this._panelOpen = true;
  }
/**
 * Closes the overlay panel and focuses the host element.
 * @return {?}
 */
close(): void {
    if (this._panelOpen) {
      this._panelOpen = false;

      if (this._selectionModel.isEmpty()) {
        this._placeholderState = '';
      }

      this._focusHost();
    }
  }
/**
 * Sets the select's value. Part of the ControlValueAccessor interface
 * required to integrate with Angular's core forms API.
 * 
 * @param {?} value New value to be written to the model.
 * @return {?}
 */
writeValue(value: any): void {
    if (this.options) {
      this._setSelectionByValue(value);
    }
  }
/**
 * Saves a callback function to be invoked when the select's value
 * changes from user input. Part of the ControlValueAccessor interface
 * required to integrate with Angular's core forms API.
 * 
 * @param {?} fn Callback to be triggered when the value changes.
 * @return {?}
 */
registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }
/**
 * Saves a callback function to be invoked when the select is blurred
 * by the user. Part of the ControlValueAccessor interface required
 * to integrate with Angular's core forms API.
 * 
 * @param {?} fn Callback to be triggered when the component has been touched.
 * @return {?}
 */
registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }
/**
 * Disables the select. Part of the ControlValueAccessor interface required
 * to integrate with Angular's core forms API.
 * 
 * @param {?} isDisabled Sets whether the component is disabled.
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
/**
 * Whether or not the overlay panel is open.
 * @return {?}
 */
get panelOpen(): boolean {
    return this._panelOpen;
  }
/**
 * The currently selected option.
 * @return {?}
 */
get selected(): MdOption | MdOption[] {
    return this.multiple ? this._selectionModel.selected : this._selectionModel.selected[0];
  }
/**
 * The value displayed in the trigger.
 * @return {?}
 */
get triggerValue(): string {
    if (this._multiple) {
      let /** @type {?} */ selectedOptions = this._selectionModel.selected.map(option => option.viewValue);

      if (this._isRtl()) {
        selectedOptions.reverse();
      }

      // TODO(crisbeto): delimiter should be configurable for proper localization.
      return selectedOptions.join(', ');
    }

    return this._selectionModel.selected[0].viewValue;
  }
/**
 * Whether the element is in RTL mode.
 * @return {?}
 */
_isRtl(): boolean {
    return this._dir ? this._dir.value === 'rtl' : false;
  }
/**
 * Sets the width of the trigger element. This is necessary to match
 * the overlay width to the trigger width.
 * @return {?}
 */
private _setTriggerWidth(): void {
    this._triggerWidth = this._getTriggerRect().width;
  }
/**
 * Handles the keyboard interactions of a closed select.
 * @param {?} event
 * @return {?}
 */
_handleClosedKeydown(event: KeyboardEvent): void {
    if (!this.disabled) {
      if (event.keyCode === ENTER || event.keyCode === SPACE) {
        event.preventDefault(); // prevents the page from scrolling down when pressing space
        this.open();
      } else if (event.keyCode === UP_ARROW || event.keyCode === DOWN_ARROW) {
        this._handleArrowKey(event);
      }
    }
  }
/**
 * Handles keypresses inside the panel.
 * @param {?} event
 * @return {?}
 */
_handlePanelKeydown(event: KeyboardEvent): void {
    if (event.keyCode === HOME || event.keyCode === END) {
      event.preventDefault();
      event.keyCode === HOME ? this._keyManager.setFirstItemActive() :
                               this._keyManager.setLastItemActive();
    } else {
      this._keyManager.onKeydown(event);
    }
  }
/**
 * When the panel element is finished transforming in (though not fading in), it
 * emits an event and focuses an option if the panel is open.
 * @return {?}
 */
_onPanelDone(): void {
    if (this.panelOpen) {
      this._focusCorrectOption();
      this.onOpen.emit();
    } else {
      this.onClose.emit();
      this._panelDoneAnimating = false;
      this.overlayDir.offsetX = 0;
    }
  }
/**
 * When the panel content is done fading in, the _panelDoneAnimating property is
 * set so the proper class can be added to the panel.
 * @return {?}
 */
_onFadeInDone(): void {
    this._panelDoneAnimating = this.panelOpen;
  }
/**
 * Calls the touched callback only if the panel is closed. Otherwise, the trigger will
 * "blur" to the panel when it opens, causing a false positive.
 * @return {?}
 */
_onBlur() {
    if (!this.panelOpen) {
      this._onTouched();
    }
  }
/**
 * Callback that is invoked when the overlay panel has been attached.
 * @return {?}
 */
_onAttached(): void {
    this._calculateOverlayOffsetX();
    this._setScrollTop();
  }
/**
 * Sets the scroll position of the scroll container. This must be called after
 * the overlay pane is attached or the scroll container element will not yet be
 * present in the DOM.
 * @return {?}
 */
private _setScrollTop(): void {
    const /** @type {?} */ scrollContainer =
        this.overlayDir.overlayRef.overlayElement.querySelector('.mat-select-panel');
    scrollContainer.scrollTop = this._scrollTop;
  }
/**
 * Sets the selected option based on a value. If no option can be
 * found with the designated value, the select trigger is cleared.
 * @param {?} value
 * @param {?=} isUserInput
 * @return {?}
 */
private _setSelectionByValue(value: any | any[], isUserInput = false): void {
    const /** @type {?} */ isArray = Array.isArray(value);

    if (this.multiple && value && !isArray) {
      throw getMdSelectNonArrayValueError();
    }

    this._clearSelection();

    if (isArray) {
      value.forEach((currentValue: any) => this._selectValue(currentValue, isUserInput));
      this._sortValues();
    } else {
      this._selectValue(value, isUserInput);
    }

    this._setValueWidth();

    if (this._selectionModel.isEmpty()) {
      this._placeholderState = '';
    }

    this._changeDetectorRef.markForCheck();
  }
/**
 * Finds and selects and option based on its value.
 * @param {?} value
 * @param {?=} isUserInput
 * @return {?} Option that has the corresponding value.
 */
private _selectValue(value: any, isUserInput = false): MdOption {
    let /** @type {?} */ optionsArray = this.options.toArray();
    let /** @type {?} */ correspondingOption = optionsArray.find(option => {
      return option.value != null && option.value === value;
    });

    if (correspondingOption) {
      isUserInput ? correspondingOption._selectViaInteraction() : correspondingOption.select();
      this._selectionModel.select(correspondingOption);
      this._keyManager.setActiveItem(optionsArray.indexOf(correspondingOption));
    }

    return correspondingOption;
  }
/**
 * Clears the select trigger and deselects every option in the list.
 * @param {?=} skip Option that should not be deselected.
 * @return {?}
 */
private _clearSelection(skip?: MdOption): void {
    this._selectionModel.clear();
    this.options.forEach(option => {
      if (option !== skip) {
        option.deselect();
      }
    });
  }
/**
 * @return {?}
 */
private _getTriggerRect(): ClientRect {
    return this.trigger.nativeElement.getBoundingClientRect();
  }
/**
 * Sets up a key manager to listen to keyboard events on the overlay panel.
 * @return {?}
 */
private _initKeyManager() {
    this._keyManager = new FocusKeyManager(this.options);
    this._tabSubscription = this._keyManager.tabOut.subscribe(() => this.close());
  }
/**
 * Drops current option subscriptions and IDs and resets from scratch.
 * @return {?}
 */
private _resetOptions(): void {
    this._dropSubscriptions();
    this._listenToOptions();
    this._setOptionIds();
    this._setOptionMultiple();
  }
/**
 * Listens to user-generated selection events on each option.
 * @return {?}
 */
private _listenToOptions(): void {
    this._optionSubscription = this.optionSelectionChanges
      .filter(event => event.isUserInput)
      .subscribe(event => {
        this._onSelect(event.source);
        this._setValueWidth();

        if (!this.multiple) {
          this.close();
        }
      });
  }
/**
 * Invoked when an option is clicked.
 * @param {?} option
 * @return {?}
 */
private _onSelect(option: MdOption): void {
    const /** @type {?} */ wasSelected = this._selectionModel.isSelected(option);

    // TODO(crisbeto): handle blank/null options inside multi-select.
    if (this.multiple) {
      this._selectionModel.toggle(option);
      wasSelected ? option.deselect() : option.select();
      this._sortValues();
    } else {
      this._clearSelection(option.value == null ? null : option);

      if (option.value == null) {
        this._propagateChanges(option.value);
      } else {
        this._selectionModel.select(option);
      }
    }

    if (wasSelected !== this._selectionModel.isSelected(option)) {
      this._propagateChanges();
    }
  }
/**
 * Sorts the model values, ensuring that they keep the same
 * order that they have in the panel.
 * @return {?}
 */
private _sortValues(): void {
    if (this._multiple) {
      this._selectionModel.clear();

      this.options.forEach(option => {
        if (option.selected) {
          this._selectionModel.select(option);
        }
      });
    }
  }
/**
 * Unsubscribes from all option subscriptions.
 * @return {?}
 */
private _dropSubscriptions(): void {
    if (this._optionSubscription) {
      this._optionSubscription.unsubscribe();
      this._optionSubscription = null;
    }
  }
/**
 * Emits change event to set the model value.
 * @param {?=} fallbackValue
 * @return {?}
 */
private _propagateChanges(fallbackValue?: any): void {
    let /** @type {?} */ valueToEmit = null;

    if (Array.isArray(this.selected)) {
      valueToEmit = this.selected.map(option => option.value);
    } else {
      valueToEmit = this.selected ? this.selected.value : fallbackValue;
    }

    this._onChange(valueToEmit);
    this.change.emit(new MdSelectChange(this, valueToEmit));
  }
/**
 * Records option IDs to pass to the aria-owns property.
 * @return {?}
 */
private _setOptionIds() {
    this._optionIds = this.options.map(option => option.id).join(' ');
  }
/**
 * Sets the `multiple` property on each option. The promise is necessary
 * in order to avoid Angular errors when modifying the property after init.
 * @return {?}
 */
private _setOptionMultiple() {
    if (this.multiple) {
      Promise.resolve(null).then(() => {
        this.options.forEach(option => option.multiple = this.multiple);
      });
    }
  }
/**
 * Must set the width of the selected option's value programmatically
 * because it is absolutely positioned and otherwise will not clip
 * overflow. The selection arrow is 9px wide, add 4px of padding = 13
 * @return {?}
 */
private _setValueWidth() {
    this._selectedValueWidth =  this._triggerWidth - 13;
  }
/**
 * Focuses the selected item. If no option is selected, it will focus
 * the first item instead.
 * @return {?}
 */
private _focusCorrectOption(): void {
    if (this._selectionModel.isEmpty()) {
      this._keyManager.setFirstItemActive();
    } else {
      this._keyManager.setActiveItem(this._getOptionIndex(this._selectionModel.selected[0]));
    }
  }
/**
 * Focuses the host element when the panel closes.
 * @return {?}
 */
private _focusHost(): void {
    this._elementRef.nativeElement.focus();
  }
/**
 * Gets the index of the provided option in the option list.
 * @param {?} option
 * @return {?}
 */
private _getOptionIndex(option: MdOption): number {
    return this.options.reduce((result: number, current: MdOption, index: number) => {
      return result === undefined ? (option === current ? index : undefined) : result;
    }, undefined);
  }
/**
 * Calculates the scroll position and x- and y-offsets of the overlay panel.
 * @return {?}
 */
private _calculateOverlayPosition(): void {
    const /** @type {?} */ items = this._getItemCount();
    const /** @type {?} */ panelHeight = Math.min(items * SELECT_ITEM_HEIGHT, SELECT_PANEL_MAX_HEIGHT);
    const /** @type {?} */ scrollContainerHeight = items * SELECT_ITEM_HEIGHT;

    // The farthest the panel can be scrolled before it hits the bottom
    const /** @type {?} */ maxScroll = scrollContainerHeight - panelHeight;

    if (this._selectionModel.hasValue()) {
      let /** @type {?} */ selectedOptionOffset = this._getOptionIndex(this._selectionModel.selected[0]);

      selectedOptionOffset += this._getLabelCountBeforeOption(selectedOptionOffset);

      // We must maintain a scroll buffer so the selected option will be scrolled to the
      // center of the overlay panel rather than the top.
      const /** @type {?} */ scrollBuffer = panelHeight / 2;
      this._scrollTop = this._calculateOverlayScroll(selectedOptionOffset, scrollBuffer, maxScroll);
      this._offsetY = this._calculateOverlayOffsetY(selectedOptionOffset, scrollBuffer, maxScroll);
    } else {
      // If no option is selected, the panel centers on the first option. In this case,
      // we must only adjust for the height difference between the option element
      // and the trigger element, then multiply it by -1 to ensure the panel moves
      // in the correct direction up the page.
      this._offsetY = (SELECT_ITEM_HEIGHT - SELECT_TRIGGER_HEIGHT) / 2 * -1;
    }

    this._checkOverlayWithinViewport(maxScroll);
  }
/**
 * Calculates the scroll position of the select's overlay panel.
 * 
 * Attempts to center the selected option in the panel. If the option is
 * too high or too low in the panel to be scrolled to the center, it clamps the
 * scroll position to the min or max scroll positions respectively.
 * @param {?} selectedIndex
 * @param {?} scrollBuffer
 * @param {?} maxScroll
 * @return {?}
 */
_calculateOverlayScroll(selectedIndex: number, scrollBuffer: number,
                          maxScroll: number): number {
    const /** @type {?} */ optionOffsetFromScrollTop = SELECT_ITEM_HEIGHT * selectedIndex;
    const /** @type {?} */ halfOptionHeight = SELECT_ITEM_HEIGHT / 2;

    // Starts at the optionOffsetFromScrollTop, which scrolls the option to the top of the
    // scroll container, then subtracts the scroll buffer to scroll the option down to
    // the center of the overlay panel. Half the option height must be re-added to the
    // scrollTop so the option is centered based on its middle, not its top edge.
    const /** @type {?} */ optimalScrollPosition = optionOffsetFromScrollTop - scrollBuffer + halfOptionHeight;
    return clampValue(0, optimalScrollPosition, maxScroll);
  }
/**
 * Figures out the appropriate animation state for the placeholder.
 * @return {?}
 */
_getPlaceholderAnimationState(): string {
    if (this.floatPlaceholder === 'never') {
      return '';
    }

    if (this.floatPlaceholder === 'always') {
      return this._floatPlaceholderState();
    }

    return this._placeholderState;
  }
/**
 * Determines the CSS `opacity` of the placeholder element.
 * @return {?}
 */
_getPlaceholderOpacity(): string {
    return (this.floatPlaceholder !== 'never' || this._selectionModel.isEmpty()) ?
        '1' : '0';
  }
/**
 * Returns the aria-label of the select component.
 * @return {?}
 */
get _ariaLabel(): string {
    // If an ariaLabelledby value has been set, the select should not overwrite the
    // `aria-labelledby` value by setting the ariaLabel to the placeholder.
    return this.ariaLabelledby ? null : this.ariaLabel || this.placeholder;
  }
/**
 * Sets the x-offset of the overlay panel in relation to the trigger's top start corner.
 * This must be adjusted to align the selected option text over the trigger text when
 * the panel opens. Will change based on LTR or RTL text direction. Note that the offset
 * can't be calculated until the panel has been attached, because we need to know the
 * content width in order to constrain the panel within the viewport.
 * @return {?}
 */
private _calculateOverlayOffsetX(): void {
    const /** @type {?} */ overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const /** @type {?} */ viewportRect = this._viewportRuler.getViewportRect();
    const /** @type {?} */ isRtl = this._isRtl();
    const /** @type {?} */ paddingWidth = this.multiple ? SELECT_MULTIPLE_PANEL_PADDING_X + SELECT_PANEL_PADDING_X :
                                         SELECT_PANEL_PADDING_X * 2;
    let /** @type {?} */ offsetX: number;

    // Adjust the offset, depending on the option padding.
    if (this.multiple) {
      offsetX = SELECT_MULTIPLE_PANEL_PADDING_X;
    } else {
      let /** @type {?} */ selected = this._selectionModel.selected[0];
      offsetX = selected && selected.group ? SELECT_PANEL_INDENT_PADDING_X : SELECT_PANEL_PADDING_X;
    }

    // Invert the offset in LTR.
    if (!isRtl) {
      offsetX *= -1;
    }

    // Determine how much the select overflows on each side.
    const /** @type {?} */ leftOverflow = 0 - (overlayRect.left + offsetX - (isRtl ? paddingWidth : 0));
    const /** @type {?} */ rightOverflow = overlayRect.right + offsetX - viewportRect.width
                          + (isRtl ? 0 : paddingWidth);

    // If the element overflows on either side, reduce the offset to allow it to fit.
    if (leftOverflow > 0) {
      offsetX += leftOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    } else if (rightOverflow > 0) {
      offsetX -= rightOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    }

    // Set the offset directly in order to avoid having to go through change detection and
    // potentially triggering "changed after it was checked" errors.
    this.overlayDir.offsetX = offsetX;
    this.overlayDir.overlayRef.updatePosition();
  }
/**
 * Calculates the y-offset of the select's overlay panel in relation to the
 * top start corner of the trigger. It has to be adjusted in order for the
 * selected option to be aligned over the trigger when the panel opens.
 * @param {?} selectedIndex
 * @param {?} scrollBuffer
 * @param {?} maxScroll
 * @return {?}
 */
private _calculateOverlayOffsetY(selectedIndex: number, scrollBuffer: number,
                                  maxScroll: number): number {
    let /** @type {?} */ optionOffsetFromPanelTop: number;

    if (this._scrollTop === 0) {
      optionOffsetFromPanelTop = selectedIndex * SELECT_ITEM_HEIGHT;
    } else if (this._scrollTop === maxScroll) {
      const /** @type {?} */ firstDisplayedIndex = this._getItemCount() - SELECT_MAX_OPTIONS_DISPLAYED;
      const /** @type {?} */ selectedDisplayIndex = selectedIndex - firstDisplayedIndex;

      // Because the panel height is longer than the height of the options alone,
      // there is always extra padding at the top or bottom of the panel. When
      // scrolled to the very bottom, this padding is at the top of the panel and
      // must be added to the offset.
      optionOffsetFromPanelTop =
          selectedDisplayIndex * SELECT_ITEM_HEIGHT + SELECT_PANEL_PADDING_Y;
    } else {
      // If the option was scrolled to the middle of the panel using a scroll buffer,
      // its offset will be the scroll buffer minus the half height that was added to
      // center it.
      optionOffsetFromPanelTop = scrollBuffer - SELECT_ITEM_HEIGHT / 2;
    }

    // The final offset is the option's offset from the top, adjusted for the height
    // difference, multiplied by -1 to ensure that the overlay moves in the correct
    // direction up the page.
    return optionOffsetFromPanelTop * -1 - SELECT_OPTION_HEIGHT_ADJUSTMENT;
  }
/**
 * Checks that the attempted overlay position will fit within the viewport.
 * If it will not fit, tries to adjust the scroll position and the associated
 * y-offset so the panel can open fully on-screen. If it still won't fit,
 * sets the offset back to 0 to allow the fallback position to take over.
 * @param {?} maxScroll
 * @return {?}
 */
private _checkOverlayWithinViewport(maxScroll: number): void {
    const /** @type {?} */ viewportRect = this._viewportRuler.getViewportRect();
    const /** @type {?} */ triggerRect = this._getTriggerRect();

    const /** @type {?} */ topSpaceAvailable = triggerRect.top - SELECT_PANEL_VIEWPORT_PADDING;
    const /** @type {?} */ bottomSpaceAvailable =
        viewportRect.height - triggerRect.bottom - SELECT_PANEL_VIEWPORT_PADDING;

    const /** @type {?} */ panelHeightTop = Math.abs(this._offsetY);
    const /** @type {?} */ totalPanelHeight =
        Math.min(this._getItemCount() * SELECT_ITEM_HEIGHT, SELECT_PANEL_MAX_HEIGHT);
    const /** @type {?} */ panelHeightBottom = totalPanelHeight - panelHeightTop - triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this._adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
    } else if (panelHeightTop > topSpaceAvailable) {
     this._adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
    } else {
      this._transformOrigin = this._getOriginBasedOnOption();
    }
  }
/**
 * Adjusts the overlay panel up to fit in the viewport.
 * @param {?} panelHeightBottom
 * @param {?} bottomSpaceAvailable
 * @return {?}
 */
private _adjustPanelUp(panelHeightBottom: number, bottomSpaceAvailable: number) {
    const /** @type {?} */ distanceBelowViewport = panelHeightBottom - bottomSpaceAvailable;

    // Scrolls the panel up by the distance it was extending past the boundary, then
    // adjusts the offset by that amount to move the panel up into the viewport.
    this._scrollTop -= distanceBelowViewport;
    this._offsetY -= distanceBelowViewport;
    this._transformOrigin = this._getOriginBasedOnOption();

    // If the panel is scrolled to the very top, it won't be able to fit the panel
    // by scrolling, so set the offset to 0 to allow the fallback position to take
    // effect.
    if (this._scrollTop <= 0) {
      this._scrollTop = 0;
      this._offsetY = 0;
      this._transformOrigin = `50% bottom 0px`;
    }
  }
/**
 * Adjusts the overlay panel down to fit in the viewport.
 * @param {?} panelHeightTop
 * @param {?} topSpaceAvailable
 * @param {?} maxScroll
 * @return {?}
 */
private _adjustPanelDown(panelHeightTop: number, topSpaceAvailable: number,
                           maxScroll: number) {
    const /** @type {?} */ distanceAboveViewport = panelHeightTop - topSpaceAvailable;

    // Scrolls the panel down by the distance it was extending past the boundary, then
    // adjusts the offset by that amount to move the panel down into the viewport.
    this._scrollTop += distanceAboveViewport;
    this._offsetY += distanceAboveViewport;
    this._transformOrigin = this._getOriginBasedOnOption();

    // If the panel is scrolled to the very bottom, it won't be able to fit the
    // panel by scrolling, so set the offset to 0 to allow the fallback position
    // to take effect.
    if (this._scrollTop >= maxScroll) {
      this._scrollTop = maxScroll;
      this._offsetY = 0;
      this._transformOrigin = `50% top 0px`;
      return;
    }
  }
/**
 * Sets the transform origin point based on the selected option.
 * @return {?}
 */
private _getOriginBasedOnOption(): string {
    const /** @type {?} */ originY =
        Math.abs(this._offsetY) - SELECT_OPTION_HEIGHT_ADJUSTMENT + SELECT_ITEM_HEIGHT / 2;
    return `50% ${originY}px 0px`;
  }
/**
 * Figures out the floating placeholder state value.
 * @return {?}
 */
private _floatPlaceholderState(): string {
    return this._isRtl() ? 'floating-rtl' : 'floating-ltr';
  }
/**
 * Handles the user pressing the arrow keys on a closed select.
 * @param {?} event
 * @return {?}
 */
private _handleArrowKey(event: KeyboardEvent): void {
    if (this._multiple) {
      event.preventDefault();
      this.open();
    } else {
      const /** @type {?} */ prevActiveItem = this._keyManager.activeItem;

      // Cycle though the select options even when the select is closed,
      // matching the behavior of the native select element.
      // TODO(crisbeto): native selects also cycle through the options with left/right arrows,
      // however the key manager only supports up/down at the moment.
      this._keyManager.onKeydown(event);

      const /** @type {?} */ currentActiveItem = /** @type {?} */(( this._keyManager.activeItem as MdOption));

      if (currentActiveItem !== prevActiveItem) {
        this._clearSelection();
        this._setSelectionByValue(currentActiveItem.value, true);
        this._propagateChanges();
      }
    }
  }
/**
 * Calculates the amount of items in the select. This includes options and group labels.
 * @return {?}
 */
private _getItemCount(): number {
    return this.options.length + this.optionGroups.length;
  }
/**
 * Calculates the amount of option group labels that precede the specified option.
 * Useful when positioning the panel, because the labels will offset the index of the
 * currently-selected option.
 * @param {?} optionIndex
 * @return {?}
 */
private _getLabelCountBeforeOption(optionIndex: number): number {
    if (this.optionGroups.length) {
      let /** @type {?} */ options = this.options.toArray();
      let /** @type {?} */ groups = this.optionGroups.toArray();
      let /** @type {?} */ groupCounter = 0;

      for (let /** @type {?} */ i = 0; i < optionIndex + 1; i++) {
        if (options[i].group && options[i].group === groups[groupCounter]) {
          groupCounter++;
        }
      }

      return groupCounter;
    }

    return 0;
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-select, mat-select',
  templateUrl: 'select.html',
  styleUrls: ['select.css'],
  inputs: ['color', 'disabled'],
  encapsulation: ViewEncapsulation.None,
  host: {
    'role': 'listbox',
    '[attr.tabindex]': 'tabIndex',
    '[attr.aria-label]': '_ariaLabel',
    '[attr.aria-labelledby]': 'ariaLabelledby',
    '[attr.aria-required]': 'required.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-invalid]': '_control?.invalid || "false"',
    '[attr.aria-owns]': '_optionIds',
    '[class.mat-select-disabled]': 'disabled',
    'class': 'mat-select',
    '(keydown)': '_handleClosedKeydown($event)',
    '(blur)': '_onBlur()',
  },
  animations: [
    transformPlaceholder,
    transformPanel,
    fadeInContent
  ],
  exportAs: 'mdSelect',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ViewportRuler, },
{type: ChangeDetectorRef, },
{type: Renderer2, },
{type: ElementRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
{type: NgControl, decorators: [{ type: Self }, { type: Optional }, ]},
{type: undefined, decorators: [{ type: Attribute, args: ['tabindex', ] }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_PLACEHOLDER_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'trigger': [{ type: ViewChild, args: ['trigger', ] },],
'overlayDir': [{ type: ViewChild, args: [ConnectedOverlayDirective, ] },],
'options': [{ type: ContentChildren, args: [MdOption, { descendants: true }, ] },],
'optionGroups': [{ type: ContentChildren, args: [MdOptgroup, ] },],
'panelClass': [{ type: Input },],
'placeholder': [{ type: Input },],
'required': [{ type: Input },],
'multiple': [{ type: Input },],
'floatPlaceholder': [{ type: Input },],
'tabIndex': [{ type: Input },],
'ariaLabel': [{ type: Input, args: ['aria-label', ] },],
'ariaLabelledby': [{ type: Input, args: ['aria-labelledby', ] },],
'onOpen': [{ type: Output },],
'onClose': [{ type: Output },],
'change': [{ type: Output },],
};
}

function MdSelect_tsickle_Closure_declarations() {
/** @type {?} */
MdSelect.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSelect.ctorParameters;
/** @type {?} */
MdSelect.propDecorators;
/**
 * Whether or not the overlay panel is open.
 * @type {?}
 */
MdSelect.prototype._panelOpen;
/**
 * Subscriptions to option events.
 * @type {?}
 */
MdSelect.prototype._optionSubscription;
/**
 * Subscription to changes in the option list.
 * @type {?}
 */
MdSelect.prototype._changeSubscription;
/**
 * Subscription to tab events while overlay is focused.
 * @type {?}
 */
MdSelect.prototype._tabSubscription;
/**
 * Whether filling out the select is required in the form.
 * @type {?}
 */
MdSelect.prototype._required;
/**
 * The scroll position of the overlay panel, calculated to center the selected option.
 * @type {?}
 */
MdSelect.prototype._scrollTop;
/**
 * The placeholder displayed in the trigger of the select.
 * @type {?}
 */
MdSelect.prototype._placeholder;
/**
 * Whether the component is in multiple selection mode.
 * @type {?}
 */
MdSelect.prototype._multiple;
/**
 * Deals with the selection logic.
 * @type {?}
 */
MdSelect.prototype._selectionModel;
/**
 * The animation state of the placeholder.
 * @type {?}
 */
MdSelect.prototype._placeholderState;
/**
 * Tab index for the element.
 * @type {?}
 */
MdSelect.prototype._tabIndex;
/**
 * Deals with configuring placeholder options
 * @type {?}
 */
MdSelect.prototype._placeholderOptions;
/**
 * The width of the trigger. Must be saved to set the min width of the overlay panel
 * and the width of the selected value.
 * @type {?}
 */
MdSelect.prototype._triggerWidth;
/**
 * Manages keyboard events for options in the panel.
 * @type {?}
 */
MdSelect.prototype._keyManager;
/**
 * The width of the selected option's value. Must be set programmatically
 * to ensure its overflow is clipped, as it's absolutely positioned.
 * @type {?}
 */
MdSelect.prototype._selectedValueWidth;
/**
 * View -> model callback called when value changes
 * @type {?}
 */
MdSelect.prototype._onChange;
/**
 * View -> model callback called when select has been touched
 * @type {?}
 */
MdSelect.prototype._onTouched;
/**
 * The IDs of child options to be passed to the aria-owns attribute.
 * @type {?}
 */
MdSelect.prototype._optionIds;
/**
 * The value of the select panel's transform-origin property.
 * @type {?}
 */
MdSelect.prototype._transformOrigin;
/**
 * Whether the panel's animation is done.
 * @type {?}
 */
MdSelect.prototype._panelDoneAnimating;
/**
 * The y-offset of the overlay panel in relation to the trigger's top start corner.
 * This must be adjusted to align the selected option text over the trigger text.
 * when the panel opens. Will change based on the y-position of the selected option.
 * @type {?}
 */
MdSelect.prototype._offsetY;
/**
 * This position config ensures that the top "start" corner of the overlay
 * is aligned with with the top "start" of the origin by default (overlapping
 * the trigger completely). If the panel cannot fit below the trigger, it
 * will fall back to a position above the trigger.
 * @type {?}
 */
MdSelect.prototype._positions;
/**
 * Trigger that opens the select.
 * @type {?}
 */
MdSelect.prototype.trigger;
/**
 * Overlay pane containing the options.
 * @type {?}
 */
MdSelect.prototype.overlayDir;
/**
 * All of the defined select options.
 * @type {?}
 */
MdSelect.prototype.options;
/**
 * All of the defined groups of options.
 * @type {?}
 */
MdSelect.prototype.optionGroups;
/**
 * Classes to be passed to the select panel. Supports the same syntax as `ngClass`.
 * @type {?}
 */
MdSelect.prototype.panelClass;
/** @type {?} */
MdSelect.prototype._floatPlaceholder;
/**
 * Aria label of the select. If not specified, the placeholder will be used as label.
 * @type {?}
 */
MdSelect.prototype.ariaLabel;
/**
 * Input that can be used to specify the `aria-labelledby` attribute.
 * @type {?}
 */
MdSelect.prototype.ariaLabelledby;
/**
 * Event emitted when the select has been opened.
 * @type {?}
 */
MdSelect.prototype.onOpen;
/**
 * Event emitted when the select has been closed.
 * @type {?}
 */
MdSelect.prototype.onClose;
/**
 * Event emitted when the selected value has been changed by the user.
 * @type {?}
 */
MdSelect.prototype.change;
/** @type {?} */
MdSelect.prototype._viewportRuler;
/** @type {?} */
MdSelect.prototype._changeDetectorRef;
/** @type {?} */
MdSelect.prototype._dir;
/** @type {?} */
MdSelect.prototype._control;
}

/**
 * Clamps a value n between min and max values.
 * @param {?} min
 * @param {?} n
 * @param {?} max
 * @return {?}
 */
function clampValue(min: number, n: number, max: number): number {
  return Math.min(Math.max(min, n), max);
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
